import { useCallback, useState, useMemo, useEffect } from 'react';
import { useHistory } from 'react-router';
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Grid,
    CircularProgress,
    Typography,
    Button
} from '@material-ui/core';
import { DeleteIcon, EditIcon } from '../../../icons';
import ActionIconButton from '../../../components/atom/ActionIconButton';

import TablePager from '../../../components/atom/TablePager';
import { useNotify } from '../../../hooks/useNotify';
import { doSwaggerCall, useSwaggerClient } from '../../../hooks/useSwaggerClient';
import AdminHeader from '../../../components/atom/AdminHeader';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import { parseXlsxSheets, downloadExampleXlsx } from '../../../utils';
import HotelsMapView from '../../../components/HotelsMapView';

const HotelsListPage = () => {
    const [search, setSearch] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [viewMode, setViewMode] = useState('list');
    const [mapHotels, setMapHotels] = useState([]);
    const [loadingMap, setLoadingMap] = useState(false);
    const [resolvingCoords, setResolvingCoords] = useState(false);
    const { notifyError, notifySuccess } = useNotify();
    const history = useHistory();
    const { showModal } = useModals();

    const [data, loading, error, setData, reloadHotels] = useSwaggerClient('hotels', 'getHotels', {
        page,
        pageSize,
        search
    });

    const [islands, loadingIslands] = useSwaggerClient('islands', 'getAllIslands', {
        pageSize: 10000
    });

    useEffect(() => {
        if (viewMode !== 'map') {
            return;
        }
        setLoadingMap(true);
        doSwaggerCall('hotels', 'getHotels', { page: 0, pageSize: 10000 })
            .then(res => setMapHotels(res?.hotels ?? []))
            .catch(() => setMapHotels([]))
            .finally(() => setLoadingMap(false));
    }, [viewMode]);

    const resolveCoordinates = useCallback(() => {
        setResolvingCoords(true);
        doSwaggerCall('hotels', 'resolveHotelCoordinates', {})
            .then(({ total, resolved, failed }) => {
                if (total === 0) {
                    notifySuccess('All hotels already have coordinates.');
                } else {
                    notifySuccess(
                        `Resolved ${resolved} of ${total} hotel(s).${failed > 0 ? ` ${failed} could not be geocoded.` : ''}`
                    );
                }
                reloadHotels();
            })
            .catch(() => notifyError('Failed to resolve coordinates.'))
            .finally(() => setResolvingCoords(false));
    }, [notifySuccess, notifyError, reloadHotels]);

    const normalizeIslandKey = s => {
        if (!s) {
            return '';
        }
        return String(s)
            .trim()
            .toLowerCase()
            .normalize('NFKD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]/g, '');
    };

    const islandIdByKey = useMemo(() => {
        const list = islands?.islands ?? [];
        return new Map(list.map(i => [normalizeIslandKey(i.name), i.id]));
    }, [islands]);

    const deleteHotel = useCallback(
        hotelId => {
            doSwaggerCall('hotels', 'deleteHotel', { hotelId })
                .then(() => {
                    notifySuccess('Hotel deleted!');
                    reloadHotels();
                })
                .catch(() => {
                    notifyError('Cannot delete hotel!');
                });
        },
        [notifySuccess, notifyError, reloadHotels]
    );

    const validateRows = useCallback(
        rows => {
            const toCreate = [];
            const invalid = [];

            for (const row of rows) {
                const islandKey = normalizeIslandKey(row.island);
                const island_id = islandIdByKey.get(islandKey);

                if (!row.name || !row.address || !island_id) {
                    invalid.push({
                        ...row,
                        reason: !island_id
                            ? `Unknown island "${row.island}"`
                            : 'Missing required field'
                    });
                    continue;
                }

                toCreate.push({
                    name: row.name,
                    island_id,
                    island: row.island,
                    address: row.address,
                    zip_code: row.zip_code ?? null,
                    longitude: row.longitude ?? null,
                    latitude: row.latitude ?? null,
                    hotel_type: row.hotel_type ?? null,
                    hotel_phone: row.hotel_phone ?? null,
                    hotel_email: row.hotel_email ?? null
                });
            }

            return { toCreate, invalid };
        },
        [islandIdByKey]
    );

    const importHotel = useCallback(
        async (toCreate, invalidCount) => {
            if (!toCreate.length) {
                notifyError('No valid hotels to import.');
                return;
            }

            try {
                await Promise.all(
                    toCreate.map(({ island, ...hotel }) =>
                        doSwaggerCall('hotels', 'createHotel', { hotel })
                    )
                );

                const msg =
                    invalidCount > 0
                        ? `Successfully imported ${toCreate.length} hotel(s). ${invalidCount} row(s) skipped.`
                        : `Successfully imported ${toCreate.length} hotel(s).`;
                notifySuccess(msg);
                reloadHotels();
            } catch (err) {
                console.error(err);
                notifyError('Hotel import failed.');
            }
        },
        [reloadHotels, notifyError, notifySuccess]
    );

    if (error) {
        return (
            <Grid container justifyContent="center">
                <h1>{error}</h1>
            </Grid>
        );
    }

    const showUpdatesOnModal = (toCreate, invalid) => {
        const allRows = [
            ...toCreate.map(r => ({ ...r, _valid: true })),
            ...invalid.map(r => ({ ...r, _valid: false }))
        ];
        return (
            <>
                <Typography
                    variant="h2"
                    style={{ justifySelf: 'center', color: 'red', marginBottom: '10px' }}
                >
                    This action is irreversible!
                </Typography>
                {invalid.length > 0 && (
                    <Typography style={{ color: 'red', marginBottom: '8px' }}>
                        {invalid.length} row(s) will be skipped due to validation errors.
                    </Typography>
                )}
                <Table style={{ border: '2px solid black' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Island</TableCell>
                            <TableCell>Address</TableCell>
                            <TableCell>ZIP Code</TableCell>
                            <TableCell>Longitude</TableCell>
                            <TableCell>Latitude</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Phone</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {allRows.map((row, i) => (
                            <TableRow
                                key={i}
                                style={{
                                    border: row._valid ? 'inherit' : '2px solid red'
                                }}
                            >
                                <TableCell>{row.name || 'Not added'}</TableCell>
                                <TableCell>{row.island || 'Not added'}</TableCell>
                                <TableCell>{row.address || 'Not added'}</TableCell>
                                <TableCell>{row.zip_code || 'Not added'}</TableCell>
                                <TableCell>{row.longitude ?? 'Not added'}</TableCell>
                                <TableCell>{row.latitude ?? 'Not added'}</TableCell>
                                <TableCell>{row.hotel_type || 'Not added'}</TableCell>
                                <TableCell>{row.hotel_phone || 'Not added'}</TableCell>
                                <TableCell>{row.hotel_email || 'Not added'}</TableCell>
                                <TableCell style={{ color: row._valid ? 'green' : 'red' }}>
                                    {row._valid ? 'OK' : row.reason}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </>
        );
    };

    // Schema for hotel import
    const hotelSchema = {
        name: {
            from: ['Name', 'name'],
            default: '',
            transform: v => String(v ?? '').trim()
        },
        island: {
            from: ['Island/Region', 'Island', 'Region', 'island', 'region'],
            default: '',
            transform: v => String(v ?? '').trim()
        },
        address: {
            from: ['Address', 'address'],
            default: '',
            transform: v => String(v ?? '').trim()
        },

        zip_code: {
            from: ['Zip Code', 'ZIP Code', 'Zip', 'zip_code'],
            default: undefined,
            transform: v => {
                const s = String(v ?? '').trim();
                return s ? s : undefined;
            }
        },
        longitude: {
            from: ['Longitude', 'longitude'],
            default: undefined,
            transform: v => {
                const num = Number(v);
                return isNaN(num) ? undefined : num;
            }
        },
        latitude: {
            from: ['Latitude', 'latitude'],
            default: undefined,
            transform: v => {
                const num = Number(v);
                return isNaN(num) ? undefined : num;
            }
        },
        hotel_type: {
            from: ['Type', 'type', 'hotel_type'],
            default: undefined,
            transform: v => {
                const s = String(v ?? '').trim();
                return s ? s : undefined;
            }
        },
        hotel_phone: {
            from: ['Phone', 'hotel_phone', 'phone'],
            default: undefined,
            transform: v => {
                const s = String(v ?? '').trim();
                return s ? s : undefined;
            }
        },
        hotel_email: {
            from: ['Email', 'E-mail', 'hotel_email', 'email'],
            default: undefined,
            transform: v => {
                const s = String(v ?? '').trim();
                return s ? s : undefined;
            }
        }
    };

    const handleFile = ev => {
        const file = ev.target.files?.[0];
        if (!file) {
            return;
        }

        parseXlsxSheets(
            file,
            rows => {
                const { toCreate, invalid } = validateRows(rows);
                showModal(MODAL_TYPE.CONFIRMATION, {
                    title: 'Check your updates',
                    content: showUpdatesOnModal(toCreate, invalid),
                    confirm: () => importHotel(toCreate, invalid.length),
                    maxWidth: 'xl'
                });
            },
            err => console.error('Error in file processing', err),
            { schema: hotelSchema }
        );
        ev.target.value = '';
    };

    const handleDownloadSample = () => {
        const exampleData = [
            {
                Name: 'Hilton Hawaiian Village',
                Island: 'Oahu',
                Address: '2005 Kalia Rd, Honolulu, HI',
                'Zip Code': '96815',
                Longitude: '-157.8583',
                Latitude: '21.2767',
                Type: 'Resort',
                Phone: '808-949-4321',
                Email: 'reservations@hiltonhawaii.com'
            },
            {
                Name: 'Grand Hyatt Kauai',
                Island: 'Kauai',
                Address: '1571 Poipu Rd, Koloa, HI',
                'Zip Code': '96756',
                Longitude: '-159.4641',
                Latitude: '21.8752',
                Type: 'Resort',
                Phone: '808-742-1234',
                Email: 'kauai.grand@hyatt.com'
            }
        ];
        downloadExampleXlsx(exampleData, 'hotel-import-example.xlsx');
    };

    return (
        <Grid container className="bg-white client-table page" alignItems="center" spacing={2}>
            <AdminHeader
                title="Hotels"
                search={search}
                onSearch={newSearch => {
                    setPage(0);
                    setSearch(newSearch);
                }}
                createLabel="New hotel"
                onCreate={() => history.push('/admin/hotels/new')}
                onImport={loadingIslands ? null : handleFile}
                importLabel={loadingIslands ? 'Loading...' : 'Import hotels'}
                onDownloadSample={handleDownloadSample}
                downloadSampleLabel="Example file"
            />
            <Grid item xs={12} style={{ display: 'flex', gap: 8, paddingBottom: 8, alignItems: 'center' }}>
                <Button
                    variant={viewMode === 'list' ? 'contained' : 'outlined'}
                    color="primary"
                    size="small"
                    onClick={() => setViewMode('list')}
                >
                    List
                </Button>
                <Button
                    variant={viewMode === 'map' ? 'contained' : 'outlined'}
                    color="primary"
                    size="small"
                    onClick={() => setViewMode('map')}
                >
                    Map
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    disabled={resolvingCoords}
                    onClick={resolveCoordinates}
                    style={{ marginLeft: 'auto' }}
                >
                    {resolvingCoords ? 'Resolving…' : 'Resolve missing coordinates'}
                </Button>
            </Grid>
            <Grid item container xs={12} justifyContent="center">
                {viewMode === 'map' ? (
                    loadingMap ? (
                        <CircularProgress />
                    ) : (
                        <HotelsMapView hotels={mapHotels} />
                    )
                ) : (
                    <>
                        {loading && !data?.hotels?.length ? (
                            <CircularProgress />
                        ) : (
                            <Table style={{ backgroundColor: 'white' }} size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Island</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Latitude</TableCell>
                                        <TableCell>Longitude</TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data &&
                                        data.hotels &&
                                        data.hotels.map(hotel => (
                                            <TableRow key={hotel.id}>
                                                <TableCell>{hotel.name}</TableCell>
                                                <TableCell>{hotel.island.name}</TableCell>
                                                <TableCell>{hotel.hotel_type}</TableCell>
                                                <TableCell>{hotel.latitude || '-'}</TableCell>
                                                <TableCell>{hotel.longitude || '-'}</TableCell>
                                                <TableCell style={{ width: 100 }}>
                                                    <ActionIconButton
                                                        title="Edit"
                                                        icon={EditIcon}
                                                        to={`/admin/hotels/update/${hotel.id}`}
                                                    />
                                                    <ActionIconButton
                                                        title="Delete"
                                                        icon={DeleteIcon}
                                                        onClick={() => {
                                                            showModal(MODAL_TYPE.CONFIRMATION, {
                                                                title: `Are you sure you want to remove ${hotel?.name}?`,
                                                                content: 'This action is irreversible.',
                                                                confirm: () => deleteHotel(hotel.id)
                                                            });
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        )}
                        <Grid container justifyContent="flex-end">
                            <TablePager
                                total={data?.total}
                                page={page}
                                setPage={setPage}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </Grid>
    );
};

export default HotelsListPage;
